import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Satellite, ChevronLeft, ChevronRight, Info } from 'lucide-react'
import clsx from 'clsx'
import { getGrazingVerificationMap } from '../services/api'

// The 2-week grazing verdict on the ranch map: what the satellite detected,
// checked against the manager's calendar, one 2-week period at a time. Replaces
// the paddock×week heatmap — historical cells there were structurally ~0 (each
// subtracted its own baseline); a 2-week verdict re-anchored at each period is
// honest for every period, so we scrub the map through time instead.

const STATE = {
  confirmed:     { c: '#3f8a56', label: 'Confirmed grazing', desc: 'Satellite & calendar agree' },
  resting:       { c: '#cdd8c6', label: 'Resting', desc: 'Both agree — at rest' },
  unlogged:      { c: '#c2572a', label: 'Unlogged grazing', desc: 'Satellite sees grazing, none logged — the alert', flag: true },
  logged_unseen: { c: '#dea63a', label: 'Logged, not seen', desc: 'Logged, satellite didn’t confirm', flag: true },
  grazed:        { c: '#4f9d8f', label: 'Grazed (satellite)', desc: 'Satellite saw grazing — no calendar to check' },
  // Not a verdict — the absence of one. Hatched rather than tinted so it can
  // never be misread as a reading, and never styled as a flag: a coverage gap
  // is ours to fix, not something to send anyone out to the paddock over.
  insufficient_data: { c: '#e5e2dc', label: 'Not verified', desc: 'No usable satellite pass yet — we can’t say', gap: true },
}
const ORDER = ['confirmed', 'unlogged', 'logged_unseen', 'grazed', 'resting', 'insufficient_data']
// A cell we were never given is exactly as unverified as one the backend marked
// so — never fall back to 'resting', which would vouch for unobserved land.
const stateOf = (cells, code) => cells[code]?.state || 'insufficient_data'
// Legend swatch — mirrors the map fill, so the hatch reads as "no reading" in
// both places rather than as just another colour.
const swatch = (k) => (STATE[k].gap
  ? { backgroundImage: `repeating-linear-gradient(45deg, ${STATE[k].c} 0 2px, #a9a396 2px 4px)` }
  : { background: STATE[k].c })

function fmtRange(a, b) {
  const o = { month: 'short', day: 'numeric' }
  return `${new Date(a + 'T00:00:00').toLocaleDateString('en', o)} – ${new Date(b + 'T00:00:00').toLocaleDateString('en', o)}`
}

export default function GrazingVerificationMap({ ranchId, highlightPaddockId }) {
  const [idx, setIdx] = useState(0) // 0 = current 2-week period
  const { data, isLoading } = useQuery({
    queryKey: ['grazingVmap', ranchId],
    queryFn: ({ signal }) => getGrazingVerificationMap(ranchId, { signal }),
    enabled: !!ranchId,
    refetchInterval: 5 * 60_000,       // live: re-checks as new passes/entries arrive
    refetchOnWindowFocus: true,
    staleTime: 60_000,
  })

  const paddocks = data?.paddocks || []
  const period = data?.periods?.[idx]
  const live = idx === 0

  // Project all paddock rings to an SVG viewBox (equirectangular, cos-lat scaled).
  const geo = useMemo(() => {
    const rings = paddocks.map(p => {
      const bc = Array.isArray(p.boundary_coordinates) ? p.boundary_coordinates : []
      return {
        id: p.id, code: p.code, name: p.name,
        pts: bc.map(q => Array.isArray(q) ? [+q[0], +q[1]] : [+q.lng, +q.lat])
               .filter(([x, y]) => Number.isFinite(x) && Number.isFinite(y)),
      }
    }).filter(r => r.pts.length >= 3)
    if (!rings.length) return null
    const all = rings.flatMap(r => r.pts)
    const xs = all.map(p => p[0]), ys = all.map(p => p[1])
    const minx = Math.min(...xs), maxx = Math.max(...xs), miny = Math.min(...ys), maxy = Math.max(...ys)
    const cos = Math.cos((miny + maxy) / 2 * Math.PI / 180)
    const W = 720, H = 520, PAD = 24
    const sx = (maxx - minx) * cos, sy = (maxy - miny)
    const sc = Math.min((W - 2 * PAD) / (sx || 1), (H - 2 * PAD) / (sy || 1))
    const ox = (W - sx * sc) / 2, oy = (H - sy * sc) / 2
    const proj = ([x, y]) => [ox + (x - minx) * cos * sc, H - (oy + (y - miny) * sc)]
    const centroid = (pts) => {
      const P = pts.map(proj); let a = 0, cx = 0, cy = 0
      for (let i = 0; i < P.length; i++) {
        const [x0, y0] = P[i], [x1, y1] = P[(i + 1) % P.length]
        const f = x0 * y1 - x1 * y0; a += f; cx += (x0 + x1) * f; cy += (y0 + y1) * f
      }
      a *= 0.5
      if (Math.abs(a) < 1e-6) { const m = P.reduce((s, p) => [s[0] + p[0], s[1] + p[1]], [0, 0]); return [m[0] / P.length, m[1] / P.length] }
      return [cx / (6 * a), cy / (6 * a)]
    }
    return { W, H, rings: rings.map(r => ({ ...r, d: r.pts.map((p, i) => (i ? 'L' : 'M') + proj(p).join(' ')).join(' ') + 'Z', c: centroid(r.pts) })) }
  }, [paddocks])

  const cells = period?.cells || {}
  const counts = useMemo(() => {
    const c = {}
    Object.values(cells).forEach(v => { c[v.state] = (c[v.state] || 0) + 1 })
    return c
  }, [cells])
  const flags = Object.entries(cells).filter(([, v]) => STATE[v.state]?.flag)
  // A coverage gap is ours to chase; "grazed too recently to show yet" fixes
  // itself on the next overpass. Same cell colour, different message.
  const awaitingPass = Object.values(cells)
    .filter(v => v.reason === 'awaiting_post_grazing_pass').length

  return (
    <div className="bg-white rounded-lg shadow p-6 mt-6">
      <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
        <div className="flex items-center gap-2">
          <Satellite className="h-5 w-5 text-green-700" />
          <h2 className="text-lg font-semibold text-gray-900">Grazing verification</h2>
        </div>
        <div className="flex items-center gap-3">
          {period && (
            <div className="flex items-center gap-1">
              <button onClick={() => setIdx(i => Math.min(i + 1, (data.periods.length - 1)))}
                disabled={idx >= (data?.periods?.length || 1) - 1}
                className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-30" aria-label="Older period">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm tabular-nums text-gray-700 w-[110px] text-center">
                {fmtRange(period.start, period.end)}
              </span>
              <button onClick={() => setIdx(i => Math.max(i - 1, 0))} disabled={idx === 0}
                className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-30" aria-label="Newer period">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
          {live
            ? <span className="inline-flex items-center gap-1.5 text-xs text-green-700"><span className="h-2 w-2 rounded-full bg-green-600 animate-pulse" /> Live</span>
            : <span className="text-xs text-gray-400">Past period</span>}
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        The last two weeks on the ranch: what the <b>satellite</b> detected, checked against the
        <b> manager’s calendar</b>. Step back through earlier periods with the arrows.
      </p>

      {isLoading ? (
        <div className="h-64 animate-pulse bg-gray-50 rounded" />
      ) : !geo ? (
        <p className="text-sm text-gray-500 text-center py-8">No paddock boundaries to map yet.</p>
      ) : (
        <div className="grid md:grid-cols-3 gap-5">
          <div className="md:col-span-2">
            <svg viewBox={`0 0 ${geo.W} ${geo.H}`} className="w-full h-auto rounded-lg bg-gray-50" role="img"
              aria-label="Ranch paddock verification map">
              <defs>
                <pattern id="vmap-nodata" width="7" height="7" patternUnits="userSpaceOnUse"
                  patternTransform="rotate(45)">
                  <rect width="7" height="7" fill={STATE.insufficient_data.c} />
                  <line x1="0" y1="0" x2="0" y2="7" stroke="#a9a396" strokeWidth="2" />
                </pattern>
              </defs>
              {geo.rings.map(r => {
                const st = stateOf(cells, r.code)
                const s = STATE[st] || STATE.insufficient_data
                const hi = highlightPaddockId && r.id === highlightPaddockId
                return (
                  <path key={r.code} d={r.d} fill={s.gap ? 'url(#vmap-nodata)' : s.c}
                    fillOpacity={st === 'resting' ? 0.5 : 0.9}
                    stroke={hi ? '#1d4ed8' : (s.flag ? '#20241a' : '#fff')}
                    strokeWidth={hi ? 3 : (s.flag ? 1.8 : 1.2)}
                    strokeDasharray={s.flag && !hi ? '5 3' : undefined}>
                    <title>{r.code} — {s.label}{cells[r.code]?.verdict != null ? ` (verdict ${cells[r.code].verdict > 0 ? '+' : ''}${cells[r.code].verdict})` : ''}{cells[r.code]?.measured ? `\nMeasured ${cells[r.code].measured.replace('..', ' to ')}` : ''}{cells[r.code]?.ground ? `\nGround: ${cells[r.code].ground.pin_obs} pin visit${cells[r.code].ground.pin_obs > 1 ? 's' : ''}${cells[r.code].ground.dung_taps > 0 ? `, dung reported ×${cells[r.code].ground.dung_taps}` : ''}` : ''}</title>
                  </path>
                )
              })}
              {geo.rings.map(r => {
                const st = stateOf(cells, r.code)
                const on = st === 'confirmed' || st === 'unlogged'
                return (
                  <text key={r.code} x={r.c[0]} y={r.c[1] + 3} textAnchor="middle"
                    className="font-mono" style={{ fontSize: 10, fontWeight: 600, fill: on ? '#fff' : '#20241a', pointerEvents: 'none' }}>
                    {r.code.replace('P-', '')}
                  </text>
                )
              })}
            </svg>
          </div>

          <div>
            <div className="flex flex-wrap gap-1.5 mb-4">
              {ORDER.filter(k => counts[k]).map(k => (
                <span key={k} className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full bg-gray-100">
                  <span className="h-2.5 w-2.5 rounded-sm" style={swatch(k)} /> {counts[k]} {STATE[k].label}
                </span>
              ))}
            </div>
            <ul className="space-y-2.5">
              {ORDER.filter(k => counts[k]).map(k => (
                <li key={k} className="flex gap-2.5 text-sm">
                  <span className={clsx('h-4 w-4 rounded-sm flex-none mt-0.5', STATE[k].flag && 'ring-1 ring-gray-800 ring-offset-1')}
                    style={swatch(k)} />
                  <span><b className="text-gray-800">{STATE[k].label}</b><span className="block text-xs text-gray-500">{STATE[k].desc}</span></span>
                </li>
              ))}
            </ul>
            {counts.insufficient_data > 0 && (
              <p className="mt-3 text-xs text-gray-500 border-t border-gray-100 pt-3">
                <b className="text-gray-700">{counts.insufficient_data} of {Object.keys(cells).length} paddocks
                could not be checked</b> this period. Nothing is implied about them or the calendar.
                {awaitingPass > 0 && (
                  <> {awaitingPass === 1 ? 'One of them was' : `${awaitingPass} of them were`} grazed
                  too recently for the satellite to show it yet — grass changes take a few days to
                  appear, so that resolves on the next pass.</>
                )}
              </p>
            )}
            {flags.length > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-100">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Needs a look</p>
                <ul className="space-y-1.5 text-xs text-gray-600">
                  {flags.map(([code, v]) => (
                    <li key={code}>
                      <b className="text-gray-800">{code}</b>{' '}
                      {v.state === 'unlogged'
                        ? `satellite sees grazing (verdict ${v.verdict > 0 ? '+' : ''}${v.verdict}) with nothing logged — schedule a check.`
                        : `logged as grazed but the satellite verdict is ${v.verdict} — light/brief grazing or a bushy paddock; worth a quick look.`}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      <p className="mt-4 text-[11px] text-gray-400 flex items-start gap-1">
        <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
        The satellite read is the two-week verdict — the reliable call. Periods before the manager
        began logging show the satellite read only. A disagreement schedules a check, it doesn’t
        assign blame. Live in-app; reports freeze a dated snapshot.
      </p>
    </div>
  )
}
